import * as React from "react"
import * as RadixDialog from "@radix-ui/react-dialog"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"
import classNames from "classnames"
import { CloseIcon } from "../icons/CloseIcon"
import { useTranslation } from "../context/TranslationContext"
export interface DialogProps {
  title?: string | React.ReactNode
  description?: string | React.ReactNode
  modalName: string
  sidebar?: React.ReactNode
  children: React.ReactNode
  closeText?: string
  footer?: React.ReactNode
  contentClassName?: string
  headerClassName?: string
  headerWrapperClassName?: string
  footerClassName?: string
  onOpenCallback?: (event?: Event) => void
  onCloseCallback?: (event?: Event) => void
}

function Dialog({
  title,
  description,
  modalName,
  sidebar,
  children,
  footer,
  closeText,
  contentClassName,
  headerClassName,
  headerWrapperClassName,
  footerClassName,
  onOpenCallback,
  onCloseCallback
}: DialogProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const translations = useTranslation()
  closeText = closeText || translations["components.products.dialog_close"]

  const onOpen = (event?: Event) => {
    setIsOpen(true)
    onOpenCallback?.(event)
  }

  const onClose = (event?: Event) => {
    setIsOpen(false)
    onCloseCallback?.(event)
  }

  const handleOpenChange = (open: boolean) => (open ? onOpen() : onClose())

  React.useEffect(() => {
    document.addEventListener(`${modalName}ModalOpen`, onOpen)
    document.addEventListener(`${modalName}ModalClose`, onClose)

    return () => {
      document.removeEventListener(`${modalName}ModalOpen`, onOpen)
      document.removeEventListener(`${modalName}ModalClose`, onClose)
    }
  }, [])

  const contentProps = description
    ? {}
    : {
        "aria-describedby": undefined
      }

  return (
    <RadixDialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialog-overlay" />
        <RadixDialog.Content
          id={`${modalName}-dialog`}
          className={classNames(
            "variant-selector-dialog__content",
            contentClassName,
            {
              "variant-selector-dialog__content--no-image": !sidebar
            }
          )}
          {...contentProps}
        >
          {sidebar}
          <RadixDialog.Title
            className={classNames(
              "variant-selector-dialog__header",
              headerClassName
            )}
          >
            <div
              className={classNames(
                "variant-selector-dialog__header-wrapper",
                headerWrapperClassName
              )}
            >
              {React.isValidElement(title) ? (
                title
              ) : (
                <RadixDialog.Title className="variant-selector-dialog__title">
                  {title}
                </RadixDialog.Title>
              )}
              <RadixDialog.Close
                className={`variant-selector-dialog__close ${modalName}-modal__close`}
                aria-label="Close"
              >
                <CloseIcon />
                {closeText && <span>{closeText}</span>}
              </RadixDialog.Close>
            </div>
          </RadixDialog.Title>
          {description && (
            <VisuallyHidden.Root>
              <RadixDialog.Description>{description}</RadixDialog.Description>
            </VisuallyHidden.Root>
          )}
          <div
            className="variant-selector-dialog__body"
            data-dialog-body={`${modalName}-dialog-body`}
          >
            {children}
          </div>
          {footer && (
            <div
              className={classNames(
                "variant-selector-dialog__footer",
                footerClassName
              )}
            >
              <div className="variant-selector-dialog__footer-wrapper">
                {footer}
              </div>
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export default Dialog
